import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaCheckDouble,
  FaClock,
  FaFileContract,
  FaFileInvoiceDollar,
  FaInbox,
  FaMedal,
  FaPlayCircle,
  FaProjectDiagram,
  FaSlidersH,
  FaTruck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import BuyerCmQcRequirementsModal from "../../components/BuyerCmQcRequirementsModal";
import SellerCmProductionProcessModal, {
  labelForProductionStage,
} from "../../components/SellerCmProductionProcessModal";
import { findCurrentProductionStageId, sellerCmProductionPath } from "./cm/cmProductionPaths";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/seller_cm_rfqs.scss";

function extractApiErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "Request failed";
}

function unwrapQcEnvelope(res) {
  return res?.data?.data ?? res?.data ?? res;
}

/** Steps that can be advanced from this workspace (matches backend seller-action set; not shown to users). */
const SELLER_MANAGED_STEPS = new Set([
  "generate_invoice",
  "generate_eway_bill",
  "eway_bill_tracking",
  "generate_shipping_manifest",
  "accept_grn",
]);

function sellerCanMarkStepComplete(stages, progress) {
  if (!Array.isArray(stages) || !progress || typeof progress !== "object") return false;
  for (const id of stages) {
    const st = progress[id]?.status;
    if (st === "completed") continue;
    if (st === "in_progress" && SELLER_MANAGED_STEPS.has(id)) return true;
    return false;
  }
  return false;
}

function productionStatusText(st) {
  const s = String(st || "").toLowerCase();
  if (s === "completed") return "Completed";
  if (s === "in_progress") return "In progress";
  return "Pending";
}

function productionStatusClass(st) {
  const s = String(st || "").toLowerCase();
  if (s === "completed") return "cm-pp-pill--done";
  if (s === "in_progress") return "cm-pp-pill--active";
  return "cm-pp-pill--pending";
}

export default function SellerContractManufacturerRfqsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState(null);

  const [qcModalOpen, setQcModalOpen] = useState(false);
  const [qcModalRfqId, setQcModalRfqId] = useState(null);
  const [qcModalProductCode, setQcModalProductCode] = useState("");
  const [qcInitialData, setQcInitialData] = useState(null);
  const [qcModalReadOnly, setQcModalReadOnly] = useState(false);

  const [ppModalOpen, setPpModalOpen] = useState(false);
  const [ppModalRfqId, setPpModalRfqId] = useState(null);
  const [ppSubmitting, setPpSubmitting] = useState(false);

  const loadRfqs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_RFQS_RECEIVED);
      const payload = res?.data ?? {};
      const list = Array.isArray(payload?.items) ? payload.items : [];
      setItems(list);
      setPendingCount(typeof payload?.pendingCount === "number" ? payload.pendingCount : 0);
    } catch (e) {
      const m = extractApiErrorMessage(e);
      setError(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await loadRfqs();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadRfqs]);

  useEffect(() => {
    if (!qcModalOpen || !qcModalRfqId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_GET(qcModalRfqId));
        const data = unwrapQcEnvelope(res);
        const qc = data?.qc;
        const payload = qc?.qc_payload_json ?? qc?.qcPayloadJson ?? null;
        if (!cancelled) {
          setQcInitialData(payload);
          const submitted = !!payload?.sellerSubmittedForApprovalAt;
          const approved = !!payload?.buyerQcNormsApprovedAt;
          setQcModalReadOnly(submitted || approved);
        }
      } catch (e) {
        if (!cancelled) toast.error(extractApiErrorMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [qcModalOpen, qcModalRfqId]);

  async function completeCurrentProductionStep(rfqId) {
    try {
      setPpSubmitting(true);
      // Pre-check server truth so we don't POST in a stale UI state.
      try {
        const pre = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_PRODUCTION_PROCESS_GET(rfqId));
        const pp = pre?.data?.data ?? pre?.data ?? pre;
        if (!pp?.exists) {
          toast.info("Production process not started. Refreshing…");
          await loadRfqs();
          return;
        }
        if (!pp?.canCompleteCurrentStage) {
          toast.info("Nothing to complete right now. Refreshing…");
          await loadRfqs();
          return;
        }
      } catch {
        // If pre-check fails, fall back to POST (will show backend message).
      }
      await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_PRODUCTION_PROCESS_COMPLETE_STAGE(rfqId), {});
      toast.success("Progress updated");
      await loadRfqs();
    } catch (e) {
      const msg = extractApiErrorMessage(e);
      // If backend says completed but UI shows otherwise, refresh list to resync.
      if (String(msg).toLowerCase().includes("all stages are already completed")) {
        toast.info("All stages already completed. Refreshing…");
        await loadRfqs();
        return;
      }
      // Pull latest server truth to avoid stale UI button states.
      try {
        const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_PRODUCTION_PROCESS_GET(rfqId));
        const pp = res?.data?.data ?? res?.data ?? res;
        const stages = Array.isArray(pp?.stages) ? pp.stages : [];
        const progress = pp?.progress && typeof pp.progress === "object" ? pp.progress : {};
        const current = stages.find((sid) => progress?.[sid]?.status !== "completed") || null;
        toast.error(`${msg}${current ? ` (Current step: ${current.replace(/_/g, " ")})` : ""}`);
      } catch {
        toast.error(msg);
      }
      await loadRfqs();
    } finally {
      setPpSubmitting(false);
    }
  }

  function openSellerQcModal(item) {
    setQcModalProductCode(String(item?.projectTitle || "").trim() || `RFQ-${item?.id}`);
    setQcModalRfqId(item.id);
    setQcInitialData(null);
    const submitted = !!(item?.sellerQcSubmittedForApproval ?? item?.seller_qc_submitted_for_approval);
    setQcModalReadOnly(submitted);
    setQcModalOpen(true);
  }

  return (
    <div className="seller-cm-rfq-page">
      <div className="seller-cm-rfq-hero">
        <div className="seller-cm-rfq-hero-text">
          <h1 className="seller-cm-rfq-title">
            <span className="seller-cm-rfq-title-icon" aria-hidden>
              <FaFileInvoiceDollar />
            </span>
            Request for Quotations (RFQs)
          </h1>
          <p className="seller-cm-rfq-sub">Select an RFQ to review and submit your quotation.</p>
        </div>
        <div className="seller-cm-rfq-pending-pill">
          <FaInbox className="seller-cm-rfq-pending-pill__icon" aria-hidden />
          {pendingCount} Pending RFQs
        </div>
      </div>

      {loading && <p className="text-muted">Loading…</p>}
      {!loading && error && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="alert alert-info" role="alert">
          No RFQs received yet.
        </div>
      )}

      {!loading &&
        items.map((item) => (
          <div key={item.id} className="seller-cm-rfq-card">
            {(() => {
              const quotedAmount =
                item.quotedAmount ||
                item.quoteAmount ||
                item.quotationAmount ||
                item.quoted_amount ||
                (item.quoteStatus === "quoted" ? item.estimatedValue : "");
              const qcApplied = !!(item.qcApplied ?? item.qc_applied);
              const qcSubmitted = !!(item.sellerQcSubmittedForApproval ?? item.seller_qc_submitted_for_approval);
              const internalReportSubmitted = !!(
                item.internalInspectionReportSubmitted ?? item.internal_inspection_report_submitted
              );
              const qas = String(item.qualityApprovalStatus ?? item.quality_approval_status ?? "").trim().toLowerCase();
              const qaLocked = qas === "approved" || qas === "rejected";
              const showStartPp = !!(item.showStartProductionProcess ?? item.show_start_production_process);
              const buyerDeliveryAccepted = !!(item.buyerDeliveryAccepted ?? item.buyer_delivery_accepted);
              const pp = item.productionProcess ?? item.production_process;
              const ppStarted = pp?.started && Array.isArray(pp.stages);
              const canMarkPp = ppStarted && sellerCanMarkStepComplete(pp.stages, pp.progress);
              const currentProdSid = ppStarted
                ? findCurrentProductionStageId(pp.stages, pp.progress)
                : null;
              const sellerOpenPath =
                currentProdSid && pp.progress?.[currentProdSid]?.status === "in_progress"
                  ? sellerCmProductionPath(item.id, currentProdSid)
                  : null;
              return (
                <>
                  <div className="seller-cm-rfq-card-top">
                    <span className="seller-cm-rfq-id">{item.displayId}</span>
                    <span
                      className={`seller-cm-rfq-badge ${
                        item.quoteStatus === "quoted" ? "seller-cm-rfq-badge--quoted" : "seller-cm-rfq-badge--pending"
                      }`}
                    >
                      {item.quoteStatus === "quoted" ? "Quoted" : "Pending Response"}
                    </span>
                  </div>
                  <h2 className="seller-cm-rfq-project-title">{item.projectTitle}</h2>
                  <div className="seller-cm-rfq-grid">
                    <div>
                      <div className="seller-cm-rfq-field-label">Buyer Company</div>
                      <div className="seller-cm-rfq-field-value">{item.buyerCompany}</div>
                    </div>
                    <div>
                      <div className="seller-cm-rfq-field-label">Industry</div>
                      <div className="seller-cm-rfq-field-value">{item.industry}</div>
                    </div>
                    <div>
                      <div className="seller-cm-rfq-field-label">Submitted Date</div>
                      <div className="seller-cm-rfq-field-value">{item.submittedDate}</div>
                    </div>
                    <div>
                      <div className="seller-cm-rfq-field-label">Expected Start</div>
                      <div className="seller-cm-rfq-field-value">
                        {item.expectedStart}
                        {item.quoteStatus === "quoted" && quotedAmount ? ` | Quoted: ${quotedAmount}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="seller-cm-rfq-card-footer">
                    <div className="seller-cm-rfq-footer-left">
                      <div className="seller-cm-rfq-estimate">
                        <FaClock />
                        <span>
                          Estimated Value: <strong>{item.estimatedValue}</strong>
                        </span>
                      </div>
                      {ppStarted ? (
                        <div className="cm-pp-progress">
                          <div className="cm-pp-progress__title">
                            <FaProjectDiagram className="cm-pp-progress__title-icon" aria-hidden />
                            Production process
                          </div>
                          <ul className="cm-pp-progress__list">
                            {pp.stages.map((sid) => (
                              <li key={sid} className="cm-pp-progress__row">
                                <span className="cm-pp-progress__name">{labelForProductionStage(sid)}</span>
                                <span className={`cm-pp-pill ${productionStatusClass(pp.progress?.[sid]?.status)}`}>
                                  {productionStatusText(pp.progress?.[sid]?.status)}
                                </span>
                              </li>
                            ))}
                          </ul>
                          {sellerOpenPath ? (
                            <Link
                              className="seller-cm-rfq-btn-secondary"
                              to={sellerOpenPath}
                              style={{ marginTop: "0.5rem", textDecoration: "none", display: "inline-flex" }}
                            >
                              Open current production step
                            </Link>
                          ) : null}
                          {canMarkPp ? (
                            <button
                              type="button"
                              className="seller-cm-rfq-btn-secondary seller-cm-rfq-btn-secondary--compact"
                              disabled={ppSubmitting}
                              onClick={() => completeCurrentProductionStep(item.id)}
                              aria-label="Mark current step complete"
                              title="Mark current step complete"
                            >
                              <FaCheckDouble aria-hidden />
                              <span className="seller-cm-rfq-btn-secondary__tip">Mark current step complete</span>
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div className="seller-cm-rfq-card-actions" role="toolbar" aria-label="RFQ actions">
                      {buyerDeliveryAccepted ? (
                        <Link
                          to={`/seller/cm-rfqs/${item.id}/product-tracking`}
                          className="seller-cm-rfq-action seller-cm-rfq-action--tracking"
                          style={{ textDecoration: "none" }}
                          aria-label="Product tracking"
                          title="Product tracking"
                        >
                          <span className="seller-cm-rfq-action__ic" aria-hidden>
                            <FaTruck />
                          </span>
                          <span className="seller-cm-rfq-action__txt">Product tracking</span>
                        </Link>
                      ) : null}
                      <Link
                        to={`/seller/cm-rfqs/${item.id}/quote`}
                        className="seller-cm-rfq-action seller-cm-rfq-action--quote"
                        aria-label={
                          item.quoteStatus === "quoted" ? "Quoted details" : "View and submit quotation"
                        }
                        title={
                          item.quoteStatus === "quoted" ? "Quoted details" : "View & quote"
                        }
                      >
                        <span className="seller-cm-rfq-action__ic" aria-hidden>
                          <FaFileInvoiceDollar />
                        </span>
                        <span className="seller-cm-rfq-action__txt">
                          {item.quoteStatus === "quoted" ? "Quoted details" : "View & quote"}
                        </span>
                      </Link>
                      {qcApplied && internalReportSubmitted && qaLocked ? (
                        <button
                          type="button"
                          className="seller-cm-rfq-action seller-cm-rfq-action--qa"
                          onClick={() => navigate(`/seller/cm-rfqs/${item.id}/internal-inspection-report`)}
                          aria-label="Quality approval"
                          title="Quality approval"
                        >
                          <span className="seller-cm-rfq-action__ic" aria-hidden>
                            <FaMedal />
                          </span>
                          <span className="seller-cm-rfq-action__txt">Quality approval</span>
                        </button>
                      ) : null}
                      {qcApplied ? (
                        <button
                          type="button"
                          className="seller-cm-rfq-action seller-cm-rfq-action--qc"
                          onClick={() => openSellerQcModal(item)}
                          aria-label={qcSubmitted ? "QC requirements" : "Edit QC requirements"}
                          title={qcSubmitted ? "QC requirements" : "Edit QC requirements"}
                        >
                          <span className="seller-cm-rfq-action__ic" aria-hidden>
                            <FaSlidersH />
                          </span>
                          <span className="seller-cm-rfq-action__txt">
                            {qcSubmitted ? "QC requirements" : "Edit QC requirements"}
                          </span>
                        </button>
                      ) : null}
                      {showStartPp ? (
                        <button
                          type="button"
                          className="seller-cm-rfq-action seller-cm-rfq-action--production"
                          onClick={() => {
                            setPpModalRfqId(item.id);
                            setPpModalOpen(true);
                          }}
                          aria-label="Start production process"
                          title="Start production"
                        >
                          <span className="seller-cm-rfq-action__ic" aria-hidden>
                            <FaPlayCircle />
                          </span>
                          <span className="seller-cm-rfq-action__txt">Start production</span>
                        </button>
                      ) : null}
                      {(() => {
                        const agreementId = item.agreementId ?? item.agreement_id;
                        const rawStatus = String(item.agreementStatus ?? item.agreement_status ?? "").trim().toLowerCase();
                        const hasAgr = agreementId != null && Number(agreementId) > 0;
                        if (!hasAgr) return null;
                        const ag = rawStatus || "draft";
                        const label = ag === "submitted" ? "Sign agreement" : "View agreement";
                        return (
                          <Link
                            to={`/seller/cm-rfqs/${item.id}/agreement`}
                            className="seller-cm-rfq-action seller-cm-rfq-action--contract"
                            aria-label={label}
                            title={label}
                          >
                            <span className="seller-cm-rfq-action__ic" aria-hidden>
                              <FaFileContract />
                            </span>
                            <span className="seller-cm-rfq-action__txt">{label}</span>
                          </Link>
                        );
                      })()}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        ))}

      <SellerCmProductionProcessModal
        open={ppModalOpen}
        rfqId={ppModalRfqId}
        onClose={() => {
          setPpModalOpen(false);
          setPpModalRfqId(null);
        }}
        onSuccess={async () => {
          toast.success("Production process started");
          await loadRfqs();
        }}
      />

      <BuyerCmQcRequirementsModal
        open={qcModalOpen}
        productCode={qcModalProductCode}
        initialData={qcInitialData}
        readOnly={qcModalReadOnly}
        sellerMode
        sellerInternalInspectionRfqId={qcModalRfqId}
        onClose={() => {
          setQcModalOpen(false);
          setQcModalProductCode("");
          setQcModalRfqId(null);
          setQcInitialData(null);
          setQcModalReadOnly(false);
        }}
        onSave={async (payload) => {
          const action = String(payload?.action || "apply").toLowerCase();
          try {
            if (action === "template") {
              await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_TEMPLATE, { payload });
              toast.success("Template saved");
              setQcModalOpen(false);
              setQcModalProductCode("");
              setQcModalRfqId(null);
              setQcInitialData(null);
              return;
            }
            const rfqId = Number(qcModalRfqId);
            if (!Number.isFinite(rfqId) || rfqId <= 0) {
              toast.error("Invalid RFQ");
              return;
            }
            if (action === "inspectionupdate") {
              const patch = payload?.payload && typeof payload.payload === "object" ? payload.payload : {};
              await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_APPLY(rfqId), {
                mode: "inspectionUpdate",
                payload: patch,
              });
              toast.success("Inspection updated");
              const res = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_GET(rfqId));
              const data = unwrapQcEnvelope(res);
              const qc = data?.qc;
              const nextPayload = qc?.qc_payload_json ?? qc?.qcPayloadJson ?? null;
              setQcInitialData(nextPayload);
              await loadRfqs();
              return;
            }
            await CommonService.post(API_URLS.CONTRACT_MANUFACTURER.SELLER_QC_REQUIREMENTS_APPLY(rfqId), { payload });
            toast.success("Submitted for buyer approval");
            setQcModalOpen(false);
            setQcModalProductCode("");
            setQcModalRfqId(null);
            setQcInitialData(null);
            await loadRfqs();
          } catch (e) {
            toast.error(extractApiErrorMessage(e));
          }
        }}
      />
    </div>
  );
}
